import { MIN_MARGIN_AMOUNT } from '@kwenta/sdk/constants'
import { formatDollars, formatNumber, stripZeros, truncateNumbers } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'

import { type FC, type ReactNode, memo, useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import OPIcon from 'assets/svg/providers/optimism.svg'
import { TransactionButton } from 'components/Button/Button'
import ErrorView from 'components/ErrorView'
import ExtendedModal from 'components/ExtendedModal'
import NumericInput from 'components/Input/NumericInput'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import { FlexDiv, FlexDivCentered, FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import { DEFAULT_CRYPTO_DECIMALS } from 'constants/defaults'
import useWindowSize from 'hooks/useWindowSize'
import { setOpenModal, setOpenModalWithParams } from 'state/app/reducer'
import { selectShowModalWithParams } from 'state/app/selectors'
import {
	approveIsolatedMargin,
	depositSmartMargin,
	withdrawIsolatedMargin,
} from 'state/futures/isolatedMargin/actions'
import {
	selectApprovingSwapDeposit,
	selectIsCreatingIsolatedMarginAccount,
	selectIsolatedAccountEquity,
	selectSnxV2IsolatedBalanceInfo,
	selectSwapDepositAllowance,
	selectWithdrawableFromIsolatedAccount,
} from 'state/futures/isolatedMargin/selectors'
import {
	selectAccountContext,
	selectIsSubmittingCrossTransfer as selectIsSubmittingSmartTransfer,
} from 'state/futures/selectors'
import { ManageModalType } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectAbstractionWalletEnabled, selectIsSubAccountMode } from 'state/wallet/selectors'
import { ExternalLink } from 'styles/common'
import media from 'styles/media'

import SegmentedControl from 'components/SegmentedControl'
import { EVENT_NAME } from 'constants/matomoEvents'
import { useAnalytics } from 'hooks/useAnalytics'
import { DrawerTab } from 'sections/accounts/constants'
import ManageKeeperBalanceSmartMargin from '../TradeSmartMargin/ManageKeeperBalanceSmartMargin'

import OneClickTradingTab from './OneClickTrading/OneClickTradingTab'

type Props = {
	onDismiss(): void
}

const SocketBridge = dynamic(() => import('../../../components/SocketBridge'), {
	ssr: false,
})

const PLACEHOLDER = '0.00'

const PERCENT_OPTIONS = ['25%', '50%', '75%', '100%']

const FullWidthSelectorButtons = ({
	onSelect,
	options,
}: { onSelect: (index: number) => void; options: string[] }) => {
	return (
		<SelectorButtonsContainer>
			<FullWidthSegmentedControl onChange={onSelect} styleType="pill-button" values={options} />
		</SelectorButtonsContainer>
	)
}

type DepositInputProps = {
	value: string
	setValue(value: string): void
}

const CreateSmartMarginAccount = memo(() => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const { trackAccountCreation } = useAnalytics()
	const isCreatingAccount = useAppSelector(selectIsCreatingIsolatedMarginAccount)

	const handleCreateAccount = useCallback(() => {
		trackAccountCreation(EVENT_NAME.CLICK_CREATE_ACCOUNT)
		trackAccountCreation(EVENT_NAME.SUBMIT_CREATE_ACCOUNT)
		dispatch(setOpenModal('futures_create_account'))
	}, [dispatch, trackAccountCreation])

	return (
		<CreateAccountContainer>
			<ErrorView
				messageType="warn"
				message={t('futures.modals.manage.smart-margin-account-required')}
			/>
			<TransactionButton disabled={isCreatingAccount} onClick={handleCreateAccount}>
				{t('futures.modals.manage.create-smart-margin-account')}
			</TransactionButton>
		</CreateAccountContainer>
	)
})

const DepositInput: FC<DepositInputProps> = ({ value, setValue }) => {
	return (
		<DepositInputContainer>
			<DepositInputMain placeholder="0.00" value={value} onChange={(_, v) => setValue(v)} />
		</DepositInputContainer>
	)
}

export const DepositTab: FC = () => {
	const { t } = useTranslation()
	const [amount, setAmount] = useState('')
	const dispatch = useAppDispatch()
	const { trackDepositFlow, trackManageAccount } = useAnalytics()
	const asset = 'sUSD'
	const { balance } = useAppSelector(selectSnxV2IsolatedBalanceInfo)
	const allowance = useAppSelector(selectSwapDepositAllowance)
	const submitting = useAppSelector(selectIsSubmittingSmartTransfer)
	const approving = useAppSelector(selectApprovingSwapDeposit)
	const isSubAccountMode = useAppSelector(selectIsSubAccountMode)
	const abstractionWalletEnabled = useAppSelector(selectAbstractionWalletEnabled)
	const isolatedAccountEquity = useAppSelector(selectIsolatedAccountEquity)

	const needsApproval = useMemo(() => {
		const amtWei = wei(amount || 0)
		return wei(allowance).eq(0) || amtWei.gt(wei(allowance))
	}, [amount, allowance])

	const isDisabled = useMemo(() => {
		if (wei(balance).eq(0)) {
			return true
		} else if (needsApproval) {
			return approving
		} else {
			const amtWei = wei(amount || 0)
			return submitting || amtWei.eq(0) || amtWei.gt(wei(balance)) || isSubAccountMode
		}
	}, [balance, needsApproval, approving, amount, submitting, isSubAccountMode])

	const handleSubmit = useCallback(() => {
		if (needsApproval) {
			trackDepositFlow(EVENT_NAME.SUBMIT_APPROVE_TRANSFER_DEPOSIT)
			dispatch(approveIsolatedMargin())
		} else {
			trackDepositFlow(EVENT_NAME.SUBMIT_DEPOSIT)
			dispatch(depositSmartMargin(wei(amount)))
		}
	}, [dispatch, amount, needsApproval, trackDepositFlow])

	const buttonKey = useMemo(() => {
		return needsApproval
			? 'approve'
			: approving
				? 'approving'
				: abstractionWalletEnabled
					? 'transfer'
					: 'deposit'
	}, [needsApproval, approving, abstractionWalletEnabled])

	const switchToOP = useCallback(() => {
		trackManageAccount(EVENT_NAME.CLICK_MANAGE_ACCOUNT)
		dispatch(
			setOpenModalWithParams({
				type: 'futures_deposit_withdraw_isolated_margin',
				params: { tab: ManageModalType.Bridge },
			})
		)
	}, [dispatch, trackManageAccount])

	const handleSetMax = useCallback(() => {
		setAmount(truncateNumbers(balance, DEFAULT_CRYPTO_DECIMALS))
	}, [balance])

	const onSelectPercent = useCallback(
		(index: number) => {
			const percent = PERCENT_OPTIONS[index].replace('%', '')
			const margin = wei(balance).div(100).mul(percent)
			setAmount(stripZeros(truncateNumbers(margin, DEFAULT_CRYPTO_DECIMALS)))
		},
		[balance]
	)

	return (
		<FlexDivCol rowGap="30px">
			<FlexDivCol rowGap="10px">
				<BalanceContainer>
					<BalanceText>{t('futures.market.trade.margin.modal.balance')}:</BalanceText>
					<BalanceText onClick={handleSetMax} style={{ cursor: 'pointer' }}>
						<span>{formatNumber(balance, { suggestDecimals: true, floor: true })}</span> {asset}
					</BalanceText>
				</BalanceContainer>
				<DepositInput value={amount} setValue={setAmount} />
				<FullWidthSelectorButtons onSelect={onSelectPercent} options={PERCENT_OPTIONS} />
			</FlexDivCol>
			{wei(balance).lt(50) && (
				<InfoContainer rowGap="20px">
					<MinimumAmountDisclaimer>
						<Trans
							i18nKey="futures.market.trade.margin.modal.deposit.disclaimer"
							values={{ minMargin: formatDollars(MIN_MARGIN_AMOUNT, { minDecimals: 0 }) }}
						/>
					</MinimumAmountDisclaimer>
					<ErrorView
						messageType="info"
						message={''}
						messageWithIcon={
							<Trans
								i18nKey={`futures.market.trade.margin.modal.deposit.${abstractionWalletEnabled ? 'deposit-to-account' : 'bridge'}`}
								components={[<OPIcon key="op-icon" height={18} width={18} />, <b key="b" />]}
								values={{ dest: 'Optimism' }}
							/>
						}
						containerStyle={{
							cursor: 'pointer',
							display: 'flex',
							flexDirection: 'row',
							columnGap: '4px',
							backgroundColor: 'rgba(255, 4, 32, 0.15)',
							borderColor: 'rgba(255, 4, 32, 0.1)',
							marginBottom: '0px',
						}}
						onClick={
							abstractionWalletEnabled
								? () => {
										dispatch(
											setOpenModalWithParams({
												type: 'manage_wallet',
												params: { tab: DrawerTab.DEPOSIT },
											})
										)
									}
								: switchToOP
						}
					/>
				</InfoContainer>
			)}

			{abstractionWalletEnabled && (
				<FlexDivCol rowGap="10px">
					<FlexDivRowCentered>
						<Body color="secondary">
							{t('futures.market.trade.margin.modal.transfer.synthetix-account')}
						</Body>
						<Body color="primary">
							{wei(amount || 0).eq(0) || wei(amount || 0).gt(wei(balance))
								? formatNumber(balance, {
										suggestDecimals: true,
										floor: true,
									})
								: formatNumber(wei(balance).sub(wei(amount === '' ? '0' : amount)), {
										suggestDecimals: true,
										floor: true,
									})}{' '}
							{asset}
						</Body>
					</FlexDivRowCentered>
					<Divider height={1} divider />
					<FlexDivRowCentered>
						<Body color="secondary">
							{t('futures.market.trade.margin.modal.transfer.perps-v2-account-balance')}
						</Body>
						<Body color="secondary">
							{wei(amount || 0).eq(0) || wei(amount || 0).gt(wei(balance))
								? formatNumber(wei(isolatedAccountEquity), {
										suggestDecimals: true,
										floor: true,
									})
								: formatNumber(wei(isolatedAccountEquity).add(wei(amount === '' ? '0' : amount)), {
										suggestDecimals: true,
										floor: true,
									})}{' '}
							{asset}
						</Body>
					</FlexDivRowCentered>
				</FlexDivCol>
			)}
			<TransactionButton
				data-testid="futures-market-trade-deposit-margin-button"
				disabled={isDisabled}
				loading={submitting || approving}
				fullWidth
				onClick={handleSubmit}
				variant="flat"
			>
				{t(`futures.market.trade.margin.modal.buttons.${buttonKey}`)}
			</TransactionButton>
			{isSubAccountMode && (
				<ErrorView
					messageType="warn"
					message={t('futures.market.trade.confirmation.modal.disabled-deposit-delegation')}
					containerStyle={{ margin: '16px 0 0 0' }}
				/>
			)}
		</FlexDivCol>
	)
}

export const WithdrawTab: FC = () => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const [amount, setAmount] = useState('')
	const { trackWithdrawFlow } = useAnalytics()
	const snxTotalWithdrawable = useAppSelector(selectWithdrawableFromIsolatedAccount)
	const submitting = useAppSelector(selectIsSubmittingSmartTransfer)
	const isSubAccountMode = useAppSelector(selectIsSubAccountMode)
	const abstractionWalletEnabled = useAppSelector(selectAbstractionWalletEnabled)
	const { balance } = useAppSelector(selectSnxV2IsolatedBalanceInfo)

	const asset = 'sUSD'
	const handleSetMax = useCallback(() => {
		setAmount(truncateNumbers(snxTotalWithdrawable, DEFAULT_CRYPTO_DECIMALS))
	}, [snxTotalWithdrawable])

	const isDisabled = useMemo(() => {
		const amtWei = wei(amount || 0)
		return submitting || amtWei.eq(0) || amtWei.gt(snxTotalWithdrawable) || isSubAccountMode
	}, [amount, isSubAccountMode, submitting, snxTotalWithdrawable])

	const handleWithdraw = useCallback(() => {
		trackWithdrawFlow(EVENT_NAME.SUBMIT_WITHDRAW)
		dispatch(withdrawIsolatedMargin(wei(amount)))
	}, [amount, dispatch, trackWithdrawFlow])

	const onSelectPercent = useCallback(
		(index: number) => {
			const percent = PERCENT_OPTIONS[index].replace('%', '')
			const margin = wei(snxTotalWithdrawable).div(100).mul(percent)
			setAmount(stripZeros(truncateNumbers(margin, DEFAULT_CRYPTO_DECIMALS)))
		},
		[snxTotalWithdrawable]
	)

	return (
		<FlexDivCol rowGap="30px">
			<FlexDivCol rowGap="10px">
				<BalanceContainer>
					<BalanceText>{t('futures.market.trade.margin.modal.balance')}:</BalanceText>
					<BalanceText onClick={handleSetMax} style={{ cursor: 'pointer' }}>
						<span>
							{formatNumber(snxTotalWithdrawable, { suggestDecimals: true, floor: true })}
						</span>{' '}
						{asset}
					</BalanceText>
				</BalanceContainer>

				<NumericInput
					dataTestId="futures-market-trade-deposit-margin-input"
					placeholder={PLACEHOLDER}
					value={amount}
					onChange={(_, v) => setAmount(v)}
				/>
				<FullWidthSelectorButtons onSelect={onSelectPercent} options={PERCENT_OPTIONS} />
			</FlexDivCol>

			{abstractionWalletEnabled && (
				<FlexDivCol rowGap="10px">
					<FlexDivRowCentered>
						<Body color="secondary">
							{t('futures.market.trade.margin.modal.transfer.perps-v2-account-balance')}
						</Body>
						<Body color="secondary">
							{wei(amount || 0).eq(0) || wei(amount || 0).gt(snxTotalWithdrawable)
								? formatNumber(wei(snxTotalWithdrawable), {
										suggestDecimals: true,
										floor: true,
									})
								: formatNumber(wei(snxTotalWithdrawable).sub(wei(amount === '' ? '0' : amount)), {
										suggestDecimals: true,
										floor: true,
									})}{' '}
							{asset}
						</Body>
					</FlexDivRowCentered>
					<Divider height={1} divider />
					<FlexDivRowCentered>
						<Body color="secondary">
							{t('futures.market.trade.margin.modal.transfer.synthetix-account')}
						</Body>
						<Body color="primary">
							{wei(amount || 0).eq(0) || wei(amount || 0).gt(snxTotalWithdrawable)
								? formatNumber(balance, {
										suggestDecimals: true,
										floor: true,
									})
								: formatNumber(wei(balance).add(wei(amount === '' ? '0' : amount)), {
										suggestDecimals: true,
										floor: true,
									})}{' '}
							{asset}
						</Body>
					</FlexDivRowCentered>
				</FlexDivCol>
			)}
			<TransactionButton
				data-testid="futures-market-trade-deposit-margin-button"
				disabled={isDisabled}
				loading={submitting}
				fullWidth
				onClick={handleWithdraw}
				variant="flat"
			>
				{abstractionWalletEnabled
					? t('futures.market.trade.margin.modal.withdraw.transfer-button')
					: t('futures.market.trade.margin.modal.withdraw.button')}
			</TransactionButton>
			{isSubAccountMode && (
				<ErrorView
					messageType="warn"
					message={t('futures.market.trade.confirmation.modal.disabled-withdraw-delegation')}
					containerStyle={{ margin: '16px 0 0 0' }}
				/>
			)}
		</FlexDivCol>
	)
}

export const BridgeTab = ({ isSubAccountMode }: { isSubAccountMode: boolean }) => {
	const { t } = useTranslation()
	return (
		<>
			{isSubAccountMode ? (
				<ErrorView
					messageType="warn"
					message={t('futures.modals.manage.bridge-delegated-unsupported')}
				/>
			) : (
				<>
					<SocketBridge />
					<Spacer height={20} />
				</>
			)}
		</>
	)
}

const ManageSmartMarginModal: FC<Props> = ({ onDismiss }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const router = useRouter()
	const modalPrams = useAppSelector(selectShowModalWithParams)?.params

	const tab = modalPrams?.tab ?? ManageModalType.Deposit

	const isSubAccountMode = useAppSelector(selectIsSubAccountMode)
	const { accountId } = useAppSelector(selectAccountContext)
	const abstractionWalletEnabled = useAppSelector(selectAbstractionWalletEnabled)
	const setManageModalType = useCallback(
		(modalType: ManageModalType) =>
			dispatch(
				setOpenModalWithParams({
					type: 'futures_deposit_withdraw_isolated_margin',
					params: { tab: modalType },
				})
			),

		[dispatch]
	)

	const { greaterThanWidth } = useWindowSize()

	const isDesktop = greaterThanWidth('md')
	const isDashboard = useMemo(() => router.pathname.includes('/dashboard'), [router.pathname])

	const ManageMenuComponent = useMemo(() => {
		return isDesktop ? ManageMenuDesktop : ManageMenuMobile
	}, [isDesktop])

	const values: {
		title: string
		value: ManageModalType
		header: string
		suffix?: ReactNode
		component: ReactNode
		hidden?: boolean
		href?: string
	}[] = useMemo(() => {
		const list = [
			{
				title: t('futures.modals.manage.deposit-title'),
				header: accountId ? t('futures.modals.manage.deposit-header') : '',
				value: ManageModalType.Deposit,
				component: accountId ? <DepositTab /> : <CreateSmartMarginAccount />,
				showInDashboard: true,
			},
			{
				title: t('futures.modals.manage.withdraw-title'),
				value: ManageModalType.Withdraw,
				header: accountId ? t('futures.modals.manage.withdraw-header') : '',
				component: accountId ? <WithdrawTab /> : <CreateSmartMarginAccount />,
				showInDashboard: true,
			},
		]

		const orderItem = {
			title: t('futures.modals.manage.order.title'),
			header: t('futures.market.trade.orders.manage-keeper-deposit.header', { currency: 'ETH' }),
			value: ManageModalType.Orders,
			component: <ManageKeeperBalanceSmartMargin />,
			showInDashboard: !abstractionWalletEnabled,
		}

		const bridgeItem = {
			title: t('futures.modals.manage.bridge-title'),
			value: ManageModalType.Bridge,
			header: t('futures.modals.manage.bridge-header'),
			component: <BridgeTab isSubAccountMode={isSubAccountMode} />,
			showInDashboard: !abstractionWalletEnabled,
		}

		const oneclickItems = {
			title: t('futures.modals.manage.one-click-trading-title'),
			value: ManageModalType.OneClick,
			header: accountId ? t('futures.modals.manage.one-click-trading-header') : '',
			component: accountId ? <OneClickTradingTab /> : <CreateSmartMarginAccount />,
			showInDashboard: false,
		}

		if (!abstractionWalletEnabled) {
			list.push(bridgeItem)
			list.push(orderItem)
			isDesktop ? list.push(oneclickItems) : list.unshift(oneclickItems)
		}

		return list.filter((item) => (item.showInDashboard && isDashboard) || !isDashboard)
	}, [t, accountId, isSubAccountMode, isDesktop, isDashboard, abstractionWalletEnabled])

	const currentView = useMemo(() => {
		return values.find(({ value }) => value === tab)
	}, [tab, values])

	return (
		<StyledExtendedModal isOpen onDismiss={onDismiss}>
			<ManageContent>
				{!isDesktop && <ManageMenuBorder />}
				<ManageMenuComponent>
					{isDesktop && <ManageMenuTitle>{t('futures.modals.manage.title')}</ManageMenuTitle>}
					{values.map(({ title, suffix, value, hidden }) => {
						if (hidden) return null
						return (
							<ManageMenuItemButton
								key={title}
								active={value === tab}
								onClick={() => setManageModalType(value)}
							>
								{title}
								{!!suffix && <span>{suffix}</span>}
							</ManageMenuItemButton>
						)
					})}
				</ManageMenuComponent>
				{currentView && (
					<ManageView>
						<ManageViewTitle>{currentView.title}</ManageViewTitle>
						<Body color="secondary">
							<Trans
								i18nKey={currentView.header}
								components={[<Emphasis key="emphasis" href={currentView.href} />]}
							/>
						</Body>
						<Spacer height={20} />
						{currentView.component}
					</ManageView>
				)}
			</ManageContent>
		</StyledExtendedModal>
	)
}

const InfoContainer = styled(FlexDivCol)`
	margin-top: 10px;
`
const Divider = styled(Spacer)`
	width: 100%;
`
const Emphasis = styled(ExternalLink)`
	color: ${(props) => props.theme.colors.common.primaryYellow};
	cursor: pointer;
`

const ManageMenuDesktop = styled(FlexDivCol)`
	padding: 20px;
	padding-left: 0;
	height: 100%;
	gap: 20px;
	border-right: ${(props) => props.theme.colors.selectedTheme.border};
`

const ManageMenuMobile = styled(FlexDivCentered)`
	position: absolute;
	width: calc(100% - 65px);
	overflow-x: auto;
	padding: 12.5px;
	bottom: 0;
	left: 65px;
	gap: 10px;
	height: 65px;
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.tabs.position.background};
`

const ManageMenuBorder = styled.div`
	border-top: ${(props) => props.theme.colors.selectedTheme.border};
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.tabs.position.background};
	position: absolute;
	bottom: 65px;
	left: 0;
	right: 0;
	width: 100%;
	height: 1px;
`

const ManageMenuTitle = styled.h3`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.sectionHeader};
	text-transform: uppercase;
	font-weight: 900;
	font-size: 13px;
	line-height: 1.2;
	margin: 0;
`

const ManageMenuItemButton = styled.button<{ active?: boolean }>`
	white-space: nowrap;
	color: ${(props) =>
		props.theme.colors.selectedTheme.input.label[props.active ? 'primary' : 'secondary']};
	transition: background-color 0.1s ease-in-out;
	background-color: transparent;
	font-size: 15px;
	display: flex;
	width: fit-content;
	align-items: center;
	justify-content: flex-start;
	line-height: 18px;
	font-weight: 700;
	padding: 8.5px 10px;
	border-radius: 100px;
	border: none;
	cursor: pointer;
	gap: 8px;

	&:hover {
		background-color: ${(props) =>
			props.theme.colors.selectedTheme.newTheme.button.default.hover.background};
	}

	${(props) =>
		props.active &&
		css`
			background-color: ${(props) =>
				props.theme.colors.selectedTheme.newTheme.button.default.hover.background};} !important;
		`};
`

const ManageView = styled(FlexDivCol)`
	padding: 20px 0 20px 20px;

	${media.lessThan('md')`
		padding: 20px 0;
	`}
	width: 100%;
`

const ManageViewTitle = styled.h3`
	font-size: 14px;
	font-weight: 400;
	line-height: 16px;
	margin: 0 0 20px 0;
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
`

const StyledExtendedModal = styled(ExtendedModal)`
	padding: 0 20px;
	[data-reach-dialog-content] {
		margin-top: 5vh;
		width: 700px;
		height: 700px;

		${media.lessThan('md')`
			margin: 0;
			width: 100%;
			max-height: 100%;
		`}
	}
`

const ManageContent = styled(FlexDiv)`
	background-color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.containers.primary.background};
	height: 100%;
`

const BalanceContainer = styled(FlexDivRowCentered)`
	p {
		margin: 0;
	}
`

const BalanceText = styled.p<{ $gold?: boolean }>`
	color: ${(props) =>
		props.$gold ? props.theme.colors.selectedTheme.yellow : props.theme.colors.selectedTheme.gray};
	span {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`

const MinimumAmountDisclaimer = styled.div`
	padding: 10px;
	border-radius: 8px;
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.disclaimer.background};
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.disclaimer.color};
	font-size: 13px;
	text-align: center;
`

const DepositInputMain = styled(NumericInput)`
	border: 0;
	padding: 10px;
	background: transparent;
	box-shadow: none;

	input {
		font-size: 15px;
		font-weight: 400;
		font-feature-settings: 'tnum';
		letter-spacing: -1px;
	}
`

const DepositInputContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	border: ${(props) => props.theme.colors.selectedTheme.newTheme.border.style};
	border-radius: 8px;
`

const CreateAccountContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
`

const SelectorButtonsContainer = styled.div`
	width: 100%;
	display: flex;
`

const FullWidthSegmentedControl = styled(SegmentedControl)`
	width: 100%;
	
	& > div {
		width: 100%;
		display: flex;
	}
	
	& button {
		flex: 1;
	}
`

export default ManageSmartMarginModal
